import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import PageHeader, { btnPrimary, cardClass } from '../components/PageHeader';
import SearchableSelect from '../components/SearchableSelect';
import { JOB_STATUS_LABELS, jobStatusOptions } from '../utils/statusConstants';
import { PROPERTY_CATEGORY_LABELS, SERVICE_TYPE_LABELS } from '../utils/cleaningCatalog';

const JobManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState([]);
  const statusFilter = searchParams.get('status') || '';
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const [jobsRes, statsRes] = await Promise.all([
        apiFetch(`/admin/jobs${q}`),
        apiFetch('/admin/jobs/stats'),
      ]);
      setJobs(jobsRes.data || []);
      setStats(statsRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatusFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('status', value);
    else next.delete('status');
    setSearchParams(next);
  };

  return (
    <div>
      <PageHeader
        title="Jobs"
        subtitle="Work orders and cleaning visits"
        action={
          <Link to="/jobs/new" className={btnPrimary}>
            New job
          </Link>
        }
      />

      {stats.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {stats.map((s) => (
            <button
              key={s.status}
              type="button"
              onClick={() => setStatusFilter(s.status)}
              className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded hover:bg-teal-50"
            >
              {JOB_STATUS_LABELS[s.status] || s.status}: {s.count}
            </button>
          ))}
        </div>
      )}

      <SearchableSelect
        value={statusFilter}
        onChange={setStatusFilter}
        options={jobStatusOptions}
        placeholder="Filter by status…"
        allowEmpty
        emptyLabel="All statuses"
        className="max-w-xs mb-4"
      />

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <div className={cardClass}>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-5 py-3">Job #</th>
                <th className="px-5 py-3">Account</th>
                <th className="px-5 py-3">Location</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Service</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Vendor</th>
                <th className="px-5 py-3">Scheduled</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <Link to={`/jobs/${job.id}`} className="text-teal-700 font-medium">
                      {job.job_number}
                    </Link>
                  </td>
                  <td className="px-5 py-3">{job.account_name}</td>
                  <td className="px-5 py-3">{job.location_label || job.location_city}</td>
                  <td className="px-5 py-3">
                    {PROPERTY_CATEGORY_LABELS[job.property_category] ||
                      (job.account_type === 'business' ? 'Commercial' : 'Residential')}
                  </td>
                  <td className="px-5 py-3">
                    {SERVICE_TYPE_LABELS[job.service_type] || job.service_type}
                  </td>
                  <td className="px-5 py-3">{JOB_STATUS_LABELS[job.status] || job.status}</td>
                  <td className="px-5 py-3">{job.vendor_name || '—'}</td>
                  <td className="px-5 py-3">
                    {job.scheduled_start
                      ? new Date(job.scheduled_start).toLocaleString()
                      : '—'}
                  </td>
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-slate-500">
                    No jobs match this filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default JobManagement;
